import {
  DATA_CONTROLS,
  type IntentPolicyDefinition,
} from "@/api/mock-data/investment-banking/intentPolicies";
import {
  type AgentTechnology,
  type EstateAgentMcpService,
  type EstateAgentTool,
  type NativePolicyEngine,
  type NativePolicyLogic,
  type NativePolicyResource,
  type NativePolicyTarget,
  type PlannedDataPlatform,
  type PlannedMcpServer,
  type PolicyControl,
  type PolicyImplementationPlan,
  policyToolKey,
} from "@/api/temp/AgentEstateService";
import { logicalRecordId } from "@/libs/estateCounts";
import { undeployedTargetKey } from "./enforcement";
import type { AgentPolicyTargets } from "./policyTargets";

/**
 * Materialization: one policy definition becomes the native policies that
 * carry it. Mock stand-in for the policy engine's generator.
 *
 *  - one MCP server policy per server the definition reaches;
 *  - one tool policy per tool on those servers;
 *  - data-platform controls only for data sources the definition maps
 *    controls to - a source the agent merely reads through a tool gets none,
 *    because TLX already enforces there at the tool.
 *
 * Pure, so the preview (what applying will create) and the implementation
 * (what applying created) are the same function over different agents.
 */

export interface DefinitionInput {
  id: string;
  name: string;
  environmentId: string;
  outcome: string;
  controls: PolicyControl[];
  /** Present for an Intent Policy, which names its native mappings. */
  intent?: IntentPolicyDefinition;
}

export interface NativePolicyBlueprint {
  id: string;
  name: string;
  target: NativePolicyTarget;
  engine: NativePolicyEngine;
  groupId: string;
  groupName: string;
  /** See `NativePolicy.targetKey`. */
  targetKey: string;
  resource: NativePolicyResource;
  expected: NativePolicyLogic;
  registryServerId?: string;
}

/** Stable ids: the same definition, environment and target always agree. */
export const nativePolicyIds = {
  server: (environmentId: string, definitionId: string, serverId: string) =>
    logicalRecordId({
      environmentId,
      definitionId,
      destination: "MCP_SERVER",
      resourceKey: serverId,
    }),
  tool: (environmentId: string, definitionId: string, toolKey: string) =>
    logicalRecordId({
      environmentId,
      definitionId,
      destination: "MCP_TOOL",
      resourceKey: toolKey,
    }),
  data: (
    environmentId: string,
    definitionId: string,
    sourceId: string,
    controlId: string,
  ) =>
    logicalRecordId({
      environmentId,
      definitionId,
      destination: "DATA_PLATFORM",
      resourceKey: `${sourceId}:${controlId}`,
    }),
};

/** The definition a native policy id belongs to, without a lookup. */
export const parseNativePolicyId = (
  id: string,
): { environmentId: string; definitionId: string } | undefined => {
  const [prefix, environmentId, definitionId] = id.split(":");
  return prefix === "np" && environmentId && definitionId
    ? { environmentId, definitionId }
    : undefined;
};

export const isDataPlatformPolicyId = (id: string) => id.includes(":data:");

const allowSubject = (definition: DefinitionInput, fallback: string) => {
  const labels =
    definition.intent?.allow ??
    definition.controls
      .filter(control => control.sense === "ALLOW")
      .map(control => control.label);
  return labels.length ? labels.join(", ") : fallback;
};

function serverLogic(
  definition: DefinitionInput,
  service: EstateAgentMcpService,
): NativePolicyLogic {
  return {
    effect: "ALLOW",
    subject: allowSubject(definition, service.name),
    rule:
      definition.intent?.mcp[service.id]?.serverPolicyName ?? definition.outcome,
  };
}

function toolLogic(
  definition: DefinitionInput,
  service: EstateAgentMcpService,
  tool: EstateAgentTool,
): NativePolicyLogic {
  const gated = tool.classification !== "Read";
  if (definition.intent) {
    const mapping = definition.intent.mcp[service.id];
    const override = mapping?.toolOverrides[tool.name];
    return override
      ? {
          effect: gated ? "REQUIRE_APPROVAL" : "ALLOW",
          subject: tool.name,
          rule: override.rule,
        }
      : {
          effect: "ALLOW",
          subject: tool.name,
          rule: `Inherits ${mapping?.serverPolicyName ?? "the server access policy"}`,
        };
  }
  const block = definition.controls.find(control => control.sense === "BLOCK");
  return gated && block
    ? {
        effect: "REQUIRE_APPROVAL",
        subject: tool.name,
        rule: `${block.label} - approval required`,
      }
    : {
        effect: "ALLOW",
        subject: tool.name,
        rule: "Inherits the server access policy",
      };
}

/**
 * The native policies a definition generates for the union of its targets.
 * `targets` are the per-agent resolutions; `services` every server those
 * agents reach, so tool and data-source names resolve.
 */
export function materializeDefinition(
  definition: DefinitionInput,
  targets: Array<Omit<AgentPolicyTargets, "applicable">>,
  services: EstateAgentMcpService[],
): NativePolicyBlueprint[] {
  const { environmentId, id: definitionId } = definition;
  const serviceById = new Map(services.map(service => [service.id, service]));
  const toolByKey = new Map(
    services.flatMap(service =>
      service.tools.map(
        tool => [policyToolKey(service.id, tool.id), { service, tool }] as const,
      ),
    ),
  );
  const serverIds = [...new Set(targets.flatMap(item => item.mcpServiceIds))];
  const toolKeys = new Set(targets.flatMap(item => item.toolKeys));
  const sourceIds = [
    ...new Set(
      targets.flatMap(item =>
        item.dataSourceIds.filter(id => (item.controlsBySource[id] ?? 0) > 0),
      ),
    ),
  ];

  const blueprints: NativePolicyBlueprint[] = [];
  serverIds.forEach(serverId => {
    const service = serviceById.get(serverId);
    if (!service) return;
    const registryServerId = definition.intent?.mcp[serverId]?.registryServerId;
    const group = { groupId: serverId, groupName: service.name };
    blueprints.push({
      id: nativePolicyIds.server(environmentId, definitionId, serverId),
      name: `${service.name} Access`,
      target: "MCP_SERVER",
      engine: "TLX_POLICY_ENGINE",
      ...group,
      targetKey: undeployedTargetKey.server(serverId),
      resource: { id: serverId, name: service.name, technology: service.technology },
      expected: serverLogic(definition, service),
      registryServerId,
    });
    service.tools.forEach(tool => {
      const key = policyToolKey(serverId, tool.id);
      if (!toolKeys.has(key)) return;
      blueprints.push({
        id: nativePolicyIds.tool(environmentId, definitionId, key),
        name: tool.name,
        target: "MCP_TOOL",
        engine: "TLX_POLICY_ENGINE",
        ...group,
        targetKey: undeployedTargetKey.tool(serverId, tool.id),
        resource: {
          id: tool.id,
          name: tool.name,
          technology: service.technology,
          serverId,
          serverName: service.name,
          classification: tool.classification,
        },
        expected: toolLogic(definition, service, tool),
        registryServerId,
      });
    });
  });

  sourceIds.forEach(sourceId => {
    const tool = [...toolByKey.values()].find(
      item => item.tool.dataSourceId === sourceId,
    )?.tool;
    const controlIds = definition.intent?.data[sourceId]?.controlIds ?? [];
    const name = tool?.dataSourceName ?? tool?.dataAccess ?? sourceId;
    controlIds.forEach(controlId => {
      const control = DATA_CONTROLS[controlId];
      blueprints.push({
        id: nativePolicyIds.data(environmentId, definitionId, sourceId, controlId),
        name: control?.name ?? controlId,
        target: "DATA_PLATFORM",
        engine: "DATA_PLATFORM",
        groupId: sourceId,
        groupName: name,
        targetKey: undeployedTargetKey.control(sourceId, controlId),
        resource: {
          id: sourceId,
          name,
          platform: tool?.dataSourcePlatform,
          platformId: tool?.dataSourcePlatformId,
        },
        expected: {
          effect: control?.effect ?? "BLOCK",
          subject: control?.subject ?? name,
          rule: control?.description ?? "",
        },
      });
    });
  });
  return blueprints;
}

/** What one definition's blueprints will create, as the Enforcement Plan shows it. */
export function planFromBlueprints(
  definitionId: string,
  blueprints: NativePolicyBlueprint[],
  existingIds: ReadonlySet<string> = new Set(),
): PolicyImplementationPlan {
  const servers = new Map<string, PlannedMcpServer>();
  const platforms = new Map<string, PlannedDataPlatform>();
  blueprints.forEach(item => {
    if (item.engine === "TLX_POLICY_ENGINE") {
      const server = servers.get(item.groupId) ?? {
        id: item.groupId,
        name: item.groupName,
        technology: item.resource.technology as AgentTechnology,
        serverPolicyCount: 0,
        toolPolicyCount: 0,
      };
      if (item.target === "MCP_SERVER") server.serverPolicyCount += 1;
      else server.toolPolicyCount += 1;
      servers.set(item.groupId, server);
      return;
    }
    const platform = platforms.get(item.groupId) ?? {
      sourceId: item.groupId,
      name: item.groupName,
      platform: item.resource.platform ?? "",
      platformId: item.resource.platformId,
      controlNames: [],
    };
    platform.controlNames.push(item.name);
    platforms.set(item.groupId, platform);
  });
  return summarize(
    [definitionId],
    [...servers.values()],
    [...platforms.values()],
    blueprints.filter(item => existingIds.has(item.id)).length,
  );
}

function summarize(
  definitionIds: string[],
  mcpServers: PlannedMcpServer[],
  platforms: PlannedDataPlatform[],
  updatedCount: number,
): PolicyImplementationPlan {
  return {
    definitionIds,
    mcpServers,
    serverPolicyCount: mcpServers.reduce((sum, s) => sum + s.serverPolicyCount, 0),
    toolPolicyCount: mcpServers.reduce((sum, s) => sum + s.toolPolicyCount, 0),
    platforms,
    controlCount: platforms.reduce((sum, p) => sum + p.controlNames.length, 0),
    updatedCount,
  };
}

/**
 * Several definitions' plans as one. Servers and data sources are merged, so
 * "2 MCP servers" stays true when two definitions share one; the policy
 * counts add up, because each definition creates its own policies there.
 */
export function mergePlans(
  plans: PolicyImplementationPlan[],
): PolicyImplementationPlan {
  const servers = new Map<string, PlannedMcpServer>();
  const platforms = new Map<string, PlannedDataPlatform>();
  plans.forEach(plan => {
    plan.mcpServers.forEach(server => {
      const existing = servers.get(server.id);
      servers.set(
        server.id,
        existing
          ? {
              ...existing,
              serverPolicyCount: existing.serverPolicyCount + server.serverPolicyCount,
              toolPolicyCount: existing.toolPolicyCount + server.toolPolicyCount,
            }
          : { ...server },
      );
    });
    plan.platforms.forEach(platform => {
      const existing = platforms.get(platform.sourceId);
      platforms.set(
        platform.sourceId,
        existing
          ? {
              ...existing,
              controlNames: [...existing.controlNames, ...platform.controlNames],
            }
          : { ...platform, controlNames: [...platform.controlNames] },
      );
    });
  });
  return summarize(
    [...new Set(plans.flatMap(plan => plan.definitionIds))],
    [...servers.values()],
    [...platforms.values()],
    plans.reduce((sum, plan) => sum + plan.updatedCount, 0),
  );
}
